import Image from 'components/shared/image/image';
import useIsMobile from 'hooks/utils/useIsMobile';
import useCollectionSelectItemTracking from 'hooks/tracking/useCollectionSelectItemTracking';
import { useEffect, useState } from 'react';
import getShopifyData from 'lib/shopify/getShopifyData';
import { useSettings } from 'hooks';
import cn from 'classnames';
import SiteLink from 'components/shared/sitelink/sitelink';
import { addLocaleToHref } from 'lib/utils/helpers';
import ArrowRight from 'components/shared/arrow-right';
import { useRouter } from 'next/router';
import ProductCard from 'components/shared/productCard';
import styles from './productGrid.module.scss';

const ProductGrid = ({ data, locale }) => {
  const CAMPAIGN_STIEGLITZ = 'Campaign - Stieglitz';
  const { products, imagesInBetween, title, link, anchorId } = data;
  const isMobile = useIsMobile();
  const { setSaveItemSelectionList } = useSettings();
  const [allGridItems, setAllGridItems] = useState([]);
  const router = useRouter();

  useEffect(() => {
    const updateProductData = (product, shopifyData) => {
      const updatedShopifyData = shopifyData?.find(
        ({ productSKU }) => productSKU === product?.shopifyData?.variants?.[0]?.sku
      );
      if (product?.shopifyData) {
        // eslint-disable-next-line no-param-reassign
        product.shopifyData.variants[0] = {
          ...product.shopifyData.variants[0],
          ...(updatedShopifyData?.productCompareAtPrice && {
            compareAtPrice: updatedShopifyData?.productCompareAtPrice,
          }),
          outOfStock: updatedShopifyData?.outOfStock,
          ...(updatedShopifyData?.productPrice && { price: updatedShopifyData?.productPrice }),
          ...(updatedShopifyData?.productTitle && { title: updatedShopifyData?.productTitle }),
        };
      }
    };

    const filterOutOfStock = arr => {
      const filteredProducts = arr.filter(item => !item.shopifyData?.variants[0]?.outOfStock);

      return filteredProducts;
    };

    const shopifyDataRetrieval = async () => {
      const productIds = products.map(
        prod => `gid://shopify/Product/${prod.shopifyData?.variants[0].product_id}`
      );

      if (productIds.length) {
        const pdpShopifyData = await getShopifyData({ productIds, locale });
        if (pdpShopifyData.length > 0) {
          products.forEach(p => {
            updateProductData(p, pdpShopifyData);
            const cardData = pdpShopifyData.find(
              ({ productSKU }) => p.shopifyData?.variants[0]?.sku === productSKU
            );
            // eslint-disable-next-line no-param-reassign
            p.cardShopifyData = cardData;
          });
        }

        setAllGridItems(filterOutOfStock(products));
      } else {
        setAllGridItems(products);
      }
    };

    shopifyDataRetrieval();
  }, [products]);

  const decideInBetweenImage = index => {
    if (index === 2) return imagesInBetween[0].image;
    if (index === 8) return imagesInBetween[1].image;
    return null;
  };
  const trackSelectItem = (product, index) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useCollectionSelectItemTracking({
      product: {
        name: product?.cardShopifyData?.productTitle,
        id: product.shopifyData?.variants?.[0]?.product_id,
        category: product?.cardShopifyData?.productVendor,
        price: product?.cardShopifyData?.productPrice,
        position: index,
      },
      list: `Category: ${title || 'Product Grid'}`,
      setSaveItemSelectionList,
      locale,
    });
  };

  return (
    <section id={anchorId} className={styles.container}>
      <div className={styles.header}>
        {title && <h2 className={styles.title}>{title}</h2>}
        {link && (
          <SiteLink
            locale={locale}
            className={styles.linkToMore}
            href={addLocaleToHref(link.link.slug, locale, router)}
          >
            {isMobile ? <ArrowRight /> : link.text}
          </SiteLink>
        )}
      </div>

      <div
        className={cn(
          data.name === CAMPAIGN_STIEGLITZ ? styles.stieglitzProducts : styles.products
        )}
      >
        {allGridItems.map((product, index) => {
          if (!isMobile && (index === 2 || index === 8) && imagesInBetween.length > 0)
            return (
              <Image
                className={styles.gridItem}
                key={product?.id}
                src={decideInBetweenImage(index)}
              />
            );
          return (
            <ProductCard
              onClick={() => trackSelectItem(product, index)}
              locale={locale}
              className={styles.gridItem}
              key={product?.id}
              itemData={product}
              itemIndex={index}
              pageTitle={title || 'Product Grid'}
            />
          );
        })}
      </div>
    </section>
  );
};

export default ProductGrid;
